import { useEffect } from 'react';
import type { IntlShape } from 'react-intl';

import type { ChargingCardsSchemaType } from '@/components/steps/ChargingCardsStep';
import type { InvoiceDataSchemaType } from '@/components/steps/InvoicingStep/invoiceDataSchema';
import type { ShippingSchemaType } from '@/components/steps/ShippingAddressStep/ShippingAddressStep';
import {
    ONBOARDING_FLOW_NAME,
    trackChargingCards,
    trackFlowComplete,
    trackInvoicingData,
    trackStepAbandonment,
    trackStepView,
} from '@/features/onboarding/utils/analytics';
import { clearTracking, startStepTracking, startTracking } from '@/features/onboarding/utils/timeTracking';
import { t, translate } from '@/lib/utils';

export const useAnalytics = (selectedStep: number, completedProcess: boolean, countryIntl: IntlShape) => {
    const stepLabels = [
        countryIntl ? translate('general.chargingCards', countryIntl) : t('general.chargingCards'),
        countryIntl
            ? translate('onboarding.stepper.invoicingAddress', countryIntl)
            : t('onboarding.stepper.invoicingAddress'),
        countryIntl ? translate('general.shippingAddress', countryIntl) : t('general.shippingAddress'),
        countryIntl ? translate('onboarding.stepper.summary', countryIntl) : t('onboarding.stepper.summary'),
    ];

    useEffect(() => {
        startTracking(ONBOARDING_FLOW_NAME);
        const handleBeforeUnload = () => {
            if (!completedProcess) {
                trackStepAbandonment(selectedStep, stepLabels[selectedStep]);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            clearTracking(ONBOARDING_FLOW_NAME);
        };
    }, []);

    const handleStepView = () => {
        if (selectedStep > 0) {
            startStepTracking(ONBOARDING_FLOW_NAME, selectedStep);
            trackStepView(selectedStep, stepLabels[selectedStep]);
        }
    };

    const handleChargingCards = (state: ChargingCardsSchemaType) => {
        trackChargingCards(state);
    };

    const handleInvoicingData = (state: InvoiceDataSchemaType) => {
        trackInvoicingData(state);
    };

    const handleFlowComplete = (states: {
        chargingCardsState: ChargingCardsSchemaType;
        shippingAddressState: ShippingSchemaType;
        invoicingDataState: InvoiceDataSchemaType;
    }) => {
        trackFlowComplete('onboarding_success', {
            total_cards: states.chargingCardsState.cardsNumber,
            has_customization: states.chargingCardsState.cardsListCustomization.some(card => card.line1 || card.line2),
            has_additional_vat: states.invoicingDataState.invoicing.additional.length > 0,
            has_addressee: states.shippingAddressState.addressee !== undefined,
        });
    };

    return {
        handleStepView,
        handleChargingCards,
        handleInvoicingData,
        handleFlowComplete,
    };
};
